import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CaptureData } from '../api-client/model/captureData';
import { LineItemCaptureData } from '../api-client/model/lineItemCaptureData';
import { CaptureJobInvoice } from '../api-client/model/captureJobInvoice';
import {
  InvoiceCaptureService,
  InvoiceCaptureError,
} from '../invoice-capture/invoice-capture.service';
import { CaptureDataFormService } from '../capture-data-form/capture-data-form.service';

import { InvoiceViewerComponent } from '../invoice-viewer/invoice-viewer.component';
import { CaptureDataHeadersFormComponent } from '../capture-data-headers-form/capture-data-headers-form.component';
import { CaptureDataLineItemsFormComponent } from '../capture-data-line-items-form/capture-data-line-items-form.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrl: './main.component.css',
  imports: [
    ButtonModule,
    ToastModule,
    ReactiveFormsModule,
    TranslateModule,
    InvoiceViewerComponent,
    CaptureDataHeadersFormComponent,
    CaptureDataLineItemsFormComponent,
  ],
  providers: [MessageService],
})
export class MainComponent implements OnInit {
  title = 'Capture+';

  private readonly jobData = signal<CaptureJobInvoice | null>(null);
  private readonly router = inject(Router);
  private lastCaptureForm: FormGroup | null = null;

  protected invoiceFileUrl = computed(() => this.jobData()?.invoice?.file_url ?? '');
  protected jobId = computed(() => this.jobData()?.job_id ?? null);
  protected isJobLoaded = computed(() => this.jobData() !== null);
  protected isLoadEnd = signal<boolean>(false);
  protected resumeJobId = signal<number | null>(null);
  private readonly errorMessage = signal<string | null>(null);

  protected message = computed(() => {
    if (this.errorMessage()) return this.errorMessage();
    if (!this.isLoadEnd()) return 'Loading invoice capture job';
    if (!this.isJobLoaded()) return 'No more invoice capture jobs';
    return null;
  });

  protected captureDataForm = computed(() => {
    const job = this.jobData();
    if (job?.invoice?.captured_data) {
      this.lastCaptureForm = this.formBuilder.buildCaptureDataForm(job.invoice.captured_data);
      return this.lastCaptureForm;
    }
    return this.lastCaptureForm ?? this.formBuilder.buildEmptyFormGroup();
  });

  protected captureHeadersDataForm = computed(() => {
    const form = this.captureDataForm();
    return (form.get('headers') as FormGroup) ?? this.formBuilder.buildEmptyFormGroup();
  });

  protected captureLineItemProps: (keyof LineItemCaptureData)[] = [
    'line_number',
    'line_product_code',
    'line_description',
    'line_purchase_order_number',
    'line_quantity',
    'line_unit_code',
    'line_unit_price',
    'line_vat_amount',
    'line_vat_rate_percent',
    'line_total_vat_excluded_amount',
  ];

  private readonly formBuilder = inject(CaptureDataFormService);
  private readonly messageService = inject(MessageService);
  private readonly authService = inject(AuthenticatorService);
  private readonly invoiceCapture = inject(InvoiceCaptureService);

  ngOnInit(): void {
    this.loadJob();
  }

  private async loadJob(): Promise<void> {
    this.isLoadEnd.set(false);
    try {
      const resumeJob = await this.invoiceCapture.getJob();
      this.resumeJobId.set(resumeJob?.job_id ?? null);

      if (resumeJob) {
        this.jobData.set(resumeJob);
      } else {
        this.errorMessage.set('No job to resume.');
        this.jobData.set(null);
      }
    } catch {
      this.errorMessage.set('Failed to load job from backend.');
    } finally {
      this.isLoadEnd.set(true);
    }
  }

  loadNextCaptureJob() {
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: `Only job ID ${this.resumeJobId()} is supported for now.`,
    });
  }

  signOut() {
    this.authService.signOut();
  }

  onSaveCaptureDataForm() {
    const form = this.captureDataForm();
    if (form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Capture data has fields with errors',
      });
      return;
    }

    const data = form.value as CaptureData;
    this.invoiceCapture
      .saveCaptureData(this.jobId()!, data)
      .then(() => {
        this.jobData.update(job => ({
          ...job!,
          invoice: {
            ...job!.invoice,
            captured_data: data,
          },
        }));
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Capture data saved',
        });
      })
      .catch((error: InvoiceCaptureError) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.title,
        });
      });
  }

  onCompleteJob() {
    const form = this.captureDataForm();
    if (form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Capture data has fields with errors',
      });
      return;
    }

    const data = form.value as CaptureData;
    this.invoiceCapture
      .completeJob(this.jobId()!, data)
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Capture job completed',
        });
        this.loadNextCaptureJob();
      })
      .catch((error: InvoiceCaptureError) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.title,
        });
      });
  }

  goBackToList() {
    this.router.navigate(['invoices']);
  }
}
